import { Navigate, Route, Routes } from 'react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useAuth } from './auth/AuthContext';
import { hasRole, useMe } from './api/hooks';
import { isForbidden, describeError } from './api/errors';
import { AppShell } from './layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { NewBatchPage } from './pages/NewBatchPage';
import { BatchPage } from './pages/BatchPage';
import { BatchHistoryPage } from './pages/BatchHistoryPage';
import { AuditPage } from './pages/AuditPage';
import { AdminPage } from './pages/AdminPage';
import { EmptyState, FehlerZustand, ForbiddenState, LoadingState } from './components/States';
import { Lock } from 'lucide-react';

export function App() {
  const auth = useAuth();
  if (auth.status === 'initializing') return <LoadingState label="Anmeldung wird geprüft …" />;
  if (auth.status !== 'signedIn') return <LoginPage />;
  return <SignedIn />;
}

function SignedIn() {
  const me = useMe();
  const { logout } = useAuth();
  if (me.isPending) return <LoadingState label="Profil wird geladen …" />;
  if (me.isError) {
    if (isForbidden(me.error)) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <EmptyState
            icon={Lock}
            title="Kein Autopilot-Zugang"
            description="Ihr Konto ist angemeldet, aber für COTINGA Autopilot keiner Rolle zugewiesen. Ein Nidus-Zugang allein genügt nicht. Wenden Sie sich an einen Autopilot-Admin."
            action={<Button variant="outlined" onClick={() => void logout()}>Abmelden</Button>}
          />
        </Box>
      );
    }
    const d = describeError(me.error, 'Profil laden');
    return <FehlerZustand title={d.title} description={`${d.message} ${d.next}`} onRetry={() => void me.refetch()} />;
  }
  const isOp = hasRole(me.data, 'Operator');
  const isAdmin = hasRole(me.data, 'Admin');
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to={isOp ? '/batches/new' : '/batches'} replace />} />
        <Route path="/batches/new" element={isOp ? <NewBatchPage /> : <ForbiddenState what="das Anlegen neuer Batches" />} />
        <Route path="/batches" element={<BatchHistoryPage />} />
        <Route path="/batches/:batchId" element={<BatchPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/admin" element={isAdmin ? <Navigate to="/admin/gesellschaften" replace /> : <ForbiddenState what="die Administration" />} />
        <Route path="/admin/:tab" element={isAdmin ? <AdminPage /> : <ForbiddenState what="die Administration" />} />
        <Route path="*" element={<EmptyState title="Seite nicht gefunden" description="Diese Adresse gibt es nicht. Nutzen Sie die Navigation oben." />} />
      </Routes>
    </AppShell>
  );
}
